import { colors } from "@/styles/design-tokens";

type CountBadgeVariant = "default" | "desktop-header";

interface CountBadgeProps {
  count: number;
  /** Max count before showing "9+" (default: 9) */
  max?: number;
  variant?: CountBadgeVariant;
  className?: string;
}

/**
 * Count badge for displaying filter counts, cart counts, etc.
 * Shows the count number, or "{max}+" if count exceeds max.
 */
const COUNT_BADGE_STYLES: Record<
  CountBadgeVariant,
  {
    defaultContainer: string;
    overflowContainer: string;
    defaultText: string;
    overflowDigitText: string;
    overflowPlusText: string;
  }
> = {
  default: {
    defaultContainer: "h-6 w-6 sm:h-[23px] sm:w-[23px]",
    overflowContainer: "h-6 min-w-[26px] px-[3px] sm:h-[23px] sm:min-w-[25px] sm:px-[3px]",
    defaultText: "text-xs leading-none",
    overflowDigitText: "text-[9.5px] leading-none tabular-nums sm:text-[9.5px]",
    overflowPlusText: "-ml-[0.4px] -translate-y-[0.15px] text-[8px] leading-none sm:text-[8px]",
  },
  "desktop-header": {
    defaultContainer: "h-[19px] w-[17.5px]",
    overflowContainer: "h-[19px] min-w-[21.5px] px-[3px]",
    defaultText: "text-caption",
    overflowDigitText: "text-caption tabular-nums",
    overflowPlusText: "-ml-[0.5px] -translate-y-[0.2px] text-caption-sm leading-none",
  },
};

export function CountBadge({
  count,
  max = 9,
  variant = "default",
  className = "",
}: CountBadgeProps) {
  if (count <= 0) return null;

  const displayCount = count > max ? `${max}+` : `${count}`;
  const isOverflowCount = displayCount.endsWith("+");
  const styles = COUNT_BADGE_STYLES[variant];
  const containerClasses = isOverflowCount ? styles.overflowContainer : styles.defaultContainer;

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-white ${containerClasses} ${className}`}
      style={{ backgroundColor: colors.stroke.strongBlack }}
    >
      {isOverflowCount ? (
        <span className="inline-flex items-center justify-center leading-none">
          <span className={styles.overflowDigitText}>{displayCount.slice(0, -1)}</span>
          <span className={styles.overflowPlusText}>+</span>
        </span>
      ) : (
        <span className={styles.defaultText}>{displayCount}</span>
      )}
    </span>
  );
}
